import type { FeatureSettingName } from "./feature-settings";

interface Experiment {
    // This will become a user feature and a feature setting.  When a team
    // opts into an experiment, it sets their user feature.  To enable the
    // feature for everybody, set the feature setting.  That will also remove
    // it from the experiments panel.
    readonly id: ExperimentName;
    // If this is set, then the experiment can only be enabled or disabled
    // if this feature setting is enabled.
    readonly allowOptInFeatureSetting?: FeatureSettingName;
    readonly name: string;
    readonly description: string;
    readonly learnMoreURL?: string;
    readonly communityForumURL?: string;
    readonly kind: "experiment" | "developerTool";
}

const allExperiments = [
    {
        id: "plugin-push-notifications",
        name: "Push Notifications (Beta)",
        description: "Enables an integration to send push notifications to app users",
        learnMoreURL: "https://www.glideapps.com/docs/push-notifications",
        allowOptInFeatureSetting: "allowPluginPushNotifications",
        kind: "experiment",
    },
    {
        id: "pricingv4-triggerWebhookPlugin",
        name: "Trigger webhook plugin",
        description: "Enables the trigger webhook plugin",
        learnMoreURL: "https://www.glideapps.com/docs/automation/actions/webhook",
        allowOptInFeatureSetting: "pricingv4-allowTriggerWebhookPlugin",
        kind: "experiment",
    },
    {
        id: "newAppModalWorkflow",
        name: "New app modal workflow",
        description: "shows a new app modal workflow",
        allowOptInFeatureSetting: "allowNewAppModalWorkflow",
        kind: "experiment",
    },
    {
        id: "renderableContentComponent",
        name: "Dynamic Content component",
        description: "Render Glide components from structured data",
        allowOptInFeatureSetting: "allowRenderableContentComponent",
        kind: "experiment",
    },
    {
        id: "pricingv4-openAIv2Plugin",
        name: "OpenAI v2 plugin",
        description: "Enables OpenAI v2 Plugin",
        allowOptInFeatureSetting: "pricingv4-allowOpenAIv2Plugin",
        kind: "experiment",
    },
    {
        id: "gbtComputedColumnsAlpha",
        name: "More computed columns in Glide Big Tables",
        description: "Allow filtering by some computed columns and rollups over Template column",
        kind: "experiment",
    },
    {
        id: "gbtDeepLookups",
        name: "Lookups of Lookups in Glide Big Tables",
        description: "Allow filtering/sorting/rolling up of Lookups of Lookups in Glide Big Tables",
        kind: "experiment",
        allowOptInFeatureSetting: "allowGBTDeepLookups",
    },
    {
        id: "plugin-make",
        name: "Make integration",
        description: "Enables an integration for Make.com",
        kind: "experiment",
    },
    {
        id: "plugin-zapier",
        name: "Zapier integration",
        description: "Enables a new integration for Zapier",
        kind: "experiment",
    },
    {
        id: "pagesSelectMode",
        name: "Select mode",
        description: "Select components directly from the preview",
        kind: "experiment",
    },
    // This was previously a preview of both code component, and AI
    // component.  We're splitting them up now, and this only controls
    // the Code component.
    {
        id: "customAIComponent",
        name: "Code Component",
        description: "Create a custom component with HTML",
        allowOptInFeatureSetting: "allowCustomAIComponent",
        kind: "experiment",
    },
    {
        id: "allowAnalyzePerformanceForUsers",
        name: "Table Performance tool",
        description: "Allows builders to check table performance in Data Editor",
        kind: "developerTool",
    },
    {
        id: "columnAnalyzerForUsers",
        name: "Column Dependency Analyzer",
        description: "Enable the Column Dependency Analyzer feature",
        allowOptInFeatureSetting: "allowColumnAnalyzerForUsers",
        kind: "developerTool",
    },
    {
        id: "columnAnalyzerTools",
        name: "Column Inspector Tool",
        description: "Allows you to inspect column dependencies in Data Editor",
        allowOptInFeatureSetting: "allowColumnAnalyzerTools",
        kind: "developerTool",
    },
    {
        id: "pluginWebhookTrigger",
        name: "Webhook trigger",
        description: "Trigger workflows via webhooks",
        kind: "experiment",
    },
    {
        id: "pluginEmailTrigger",
        name: "Email trigger",
        description: "Trigger workflows via emails",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginEmailTrigger",
    },
    {
        id: "glideAPIPlugin",
        name: "Glide API plugin",
        description: "Enables the Glide API plugin for Glide Big Table data operations",
        kind: "experiment",
        allowOptInFeatureSetting: "allowGlideAPIPlugin",
    },
    {
        id: "pluginBigQuery",
        name: "BigQuery plugin",
        description: "Run BigQuery queries",
        kind: "experiment",
        allowOptInFeatureSetting: "enableBigQueryPlugin",
    },
    {
        id: "pluginSlackTrigger",
        name: "Slack trigger",
        description: "Trigger workflows with Slack messages.",
        communityForumURL:
            "https://community.glideapps.com/t/draft-glide-workflows-features-slack-trigger-manual-trigger-and-human-in-the-loop-features/81458",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginSlackTrigger",
    },
    {
        id: "redirectToCustomDomain",
        name: "Redirect to custom hostname",
        description: "Redirect to the configured custom hostname",
        kind: "experiment",
        allowOptInFeatureSetting: "allowRedirectToCustomDomain",
    },
    {
        id: "builderNotes",
        name: "Builder Notes",
        description: "Add notes to screens and components",
        kind: "experiment",
        allowOptInFeatureSetting: "allowBuilderNotes",
    },
    {
        id: "humanInTheLoop",
        name: "Human-in-the-loop features",
        description: "Request a human decision during a workflow.",
        communityForumURL:
            "https://community.glideapps.com/t/draft-glide-workflows-features-slack-trigger-manual-trigger-and-human-in-the-loop-features/81458",
        kind: "experiment",
        allowOptInFeatureSetting: "allowHumanInTheLoop",
    },
    {
        id: "iframeEmbedding",
        name: "iFrame Embedding",
        description: "Enhances embedding a Glide app within an iframe",
        kind: "experiment",
        allowOptInFeatureSetting: "allowIFrameEmbedding",
    },
    {
        id: "stopwatchComponent",
        name: "Stopwatch component",
        description: "Adds a new stopwatch component",
        kind: "experiment",
        allowOptInFeatureSetting: "allowStopwatchComponent",
    },
    {
        id: "classicAppsToPages",
        name: "Classic Apps to new Apps migration",
        description: "Lets you duplicate a Classic App as a new App",
        kind: "experiment",
        allowOptInFeatureSetting: "allowClassicAppsToPages",
    },
    {
        id: "audioEntryAction",
        name: "Record and transcribe voice in an action",
        description: "Adds an action to record and transcribe voice",
        kind: "experiment",
        allowOptInFeatureSetting: "allowAudioEntryAction",
    },
    {
        id: "querySingleColumn",
        name: "Query for single row",
        description: "Allow the Query column to get a single row, like Single Relation",
        kind: "experiment",
    },
    {
        id: "appSpecialValuesPlugin",
        name: "App special values",
        description: "Show app special values like name or icon",
        kind: "experiment",
    },
    {
        id: "deviceSpecialValuesPlugin",
        name: "Device special values",
        description: "Show device special values like screen width",
        kind: "experiment",
    },
    {
        id: "triggerAutomationAction",
        name: "Trigger Workflow step",
        description: "Enable an action step that can trigger another workflow.",
        communityForumURL:
            "https://community.glideapps.com/t/draft-glide-workflows-features-slack-trigger-manual-trigger-and-human-in-the-loop-features/81458",
        kind: "experiment",
        allowOptInFeatureSetting: "allowTriggerAutomationAction",
    },
    {
        id: "dataPlotComponent",
        name: "New Chart component",
        description: "A more powerful Chart component with more options for styling and aggregating data.",
        communityForumURL: "https://community.glideapps.com/t/draft-chart-component/81500",
        kind: "experiment",
        allowOptInFeatureSetting: "allowDataPlotComponent",
    },
    {
        id: "inlineAudioTranscript",
        name: "Inline voice transcription",
        description: "Transcribe voice in text entries and Notes components",
        kind: "experiment",
        allowOptInFeatureSetting: "allowInlineAudioTranscript",
    },
    {
        id: "stringArrayColumns",
        name: "Multiple Texts column",
        description: "Enables a new basic column type that can store an array of text strings.",
        communityForumURL: "https://community.glideapps.com/t/draft-multiple-texts-column-in-open-beta/81408",
        kind: "experiment",
        allowOptInFeatureSetting: "allowStringArrayColumns",
    },
    {
        id: "newGlideTables",
        name: "New Glide Tables",
        description: "Enable New Glide Tables as default table type",
        kind: "experiment",
        allowOptInFeatureSetting: "showNewGlideTablesInExperiments",
    },
    {
        id: "aiAgent",
        name: "AI Agent",
        description: "Vibe no-coding",
        kind: "experiment",
        allowOptInFeatureSetting: "allowAIAgent",
    },
    {
        id: "tabsComponent",
        name: "Tabs Container and Tabs Component",
        description: "Enables two new components to create tab layouts.",
        kind: "experiment",
        allowOptInFeatureSetting: "allowTabsComponent",
        communityForumURL: "https://community.glideapps.com/t/81864",
    },
    {
        id: "dataStudioDiscovery",
        name: "Ask My Data",
        description: "Use AI data insights to build beautiful views in your apps",
        kind: "experiment",
        allowOptInFeatureSetting: "allowAskMyData",
    },
    {
        id: "atcApiCallApiClone",
        name: "ATC API Call",
        description: "Interact with ATC Cloud Run Services from your app",
        kind: "experiment",
        allowOptInFeatureSetting: "allowATCAPI",
    },
    {
        id: "disableClassicApps",
        name: "Disable Classic Apps",
        description: "Makes Classic Apps inaccessible both in the builder and as apps",
        kind: "experiment",
        // This will never be on.
        // We're using an experiment so we can disable Classic Apps for a single team,
        // or by toggling the experiment globally.
        // There is also an opt-out flag, just in case we need it.
        allowOptInFeatureSetting: "allowDisableClassicApps",
    },
    {
        id: "multiplayer",
        name: "Multiplayer",
        description: "Allow multiple editors to open the same app and make changes at the same time",
        kind: "experiment",
        allowOptInFeatureSetting: "allowMultiplayer",
        communityForumURL: "https://community.glideapps.com/t/84058",
    },
    {
        id: "improvedCalendarLocalization",
        name: "Improved Calendar Localization",
        description:
            "Enhanced calendar localization with full month names and proper day abbreviations in your device language",
        kind: "experiment",
        allowOptInFeatureSetting: "allowImprovedCalendarLocalization",
    },
    {
        id: "tableSearch",
        name: "Search tables in app",
        description: "Allows users to search tables in Data Editor",
        kind: "experiment",
        allowOptInFeatureSetting: "allowTableSearch",
    },
    {
        id: "multiAppDelete",
        name: "Delete multiple apps",
        description: "Allows users to select and delete multiple apps at once from the dashboard",
        allowOptInFeatureSetting: "allowMultiAppDelete",
        kind: "developerTool",
    },
    {
        id: "automaticInactivityLogout",
        name: "Automatic inactivity logout",
        description: "Allow configuring logout after a period of inactivity",
        kind: "experiment",
    },
    {
        id: "pluginMembraneQuickbooks",
        name: "Quickbooks Plugin",
        description: "Allows using the Quickbooks integration",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginMembraneQuickbooks",
    },
    {
        id: "pluginMembraneSalesforce",
        name: "Salesforce Plugin",
        description: "Allows using the Salesforce integration",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginMembraneSalesforce",
    },
    {
        id: "pluginMembraneXero",
        name: "Xero Plugin",
        description: "Allows using the Xero integration",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginMembraneXero",
    },
    {
        id: "pluginCodeSandbox",
        name: "Code Sandbox Plugin",
        description: "Enables the Code Sandbox plugin for executing Javascript code in a secure sandbox.",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginCodeSandbox",
    },
    {
        id: "pluginSnowflake",
        name: "Snowflake",
        description: "Connect to a Snowflake database",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPluginSnowflake",
    },
    {
        id: "singleSingleSignOn",
        name: "Team-wide Single Sign-On",
        description: "Signing into one app via SSO signs you into all SSO apps in the team",
        kind: "experiment",
        allowOptInFeatureSetting: "allowSingleSingleSignOn",
    },
    {
        id: "printScreenToPDF",
        name: "Print Screen to PDF",
        description: "Adds an action to print the current screen to a PDF",
        kind: "experiment",
        allowOptInFeatureSetting: "allowPrintScreenToPDF",
    },
    {
        id: "dataEditorPlus",
        name: "Data Editor Plus",
        description: "Search/sort/filter in Data Editor",
        kind: "experiment",
        allowOptInFeatureSetting: "allowDataEditorPlus",
    },
    {
        id: "dataEditorPlusForUser",
        name: "Data Editor Plus (User)",
        description: "Enables Data Editor Plus for individual users (e.g. support agents)",
        allowOptInFeatureSetting: "allowDataEditorPlusForUser",
        kind: "developerTool",
    },
    {
        id: "appDetailsInDashboard",
        name: "App Details in Dashboard",
        description: "Shows extended app info (creation date, data sources, etc.) when hovering over apps on the dashboard",
        allowOptInFeatureSetting: "allowAppDetailsInDashboard",
        kind: "developerTool",
    },
    {
        id: "geminiGenerateImage",
        name: "Gemini image generation",
        description: "Enables the Nano Banana image generation action on the Google Gemini integration.",
        kind: "experiment",
    },
    {
        id: "consistentCustomCollectionPadding",
        name: "Consistent custom collection padding",
        description: "Aligns custom collection padding with the padding of other collections.",
        kind: "experiment",
    },
    {
        id: "typableDateField",
        name: "Typable date input",
        description: "Allow typing dates directly into the Date and Date Time components on desktop.",
        kind: "experiment",
    },
] as const;

const experimentIds = allExperiments.map(x => x.id);

export type ExperimentName = (typeof experimentIds)[number];

export const experiments: readonly Experiment[] = allExperiments;

export const experimentDefaultValues: Record<ExperimentName, boolean> = experimentIds
    .map(x => ({ [x]: false }))
    .reduce((acc, cv) => ({ ...acc, ...cv })) as Record<ExperimentName, boolean>;
